import { Box, Card, CardContent, DialogContent, Stack } from "@mui/material";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { TextButton } from "@src/appV2/redesign/components/TextButton";
import { SingleBoxFileUploader } from "@src/appV2/redesign/Placements/components/SingleBoxFileUploader/SingleBoxFileUploader";
import { CbhIcon, Illustration, Text } from "@src/appV2/redesign/system/ui-components";
import { useState } from "react";
import { useFormContext } from "react-hook-form";

import {
  type LeaveReviewEntrypoint,
  type PostShiftReviewFormValues,
  type StepAction,
} from "../types";

export interface VerifyEmploymentStepProps {
  isSubmitting?: boolean;
  onAction: (action: StepAction) => void;
  workplaceId: string;
  shiftId?: string;
  source: LeaveReviewEntrypoint;
}

const ACCEPTED_DOCUMENTS = [
  { label: "Pay stub", iconType: "document" as const },
  { label: "Offer letter", iconType: "document" as const },
  { label: "Employment contract or badge", iconType: "document-id-outlined" as const },
];

export function VerifyEmploymentStep(props: VerifyEmploymentStepProps) {
  const { isSubmitting = false, onAction, workplaceId, shiftId, source } = props;
  const { setValue } = useFormContext<PostShiftReviewFormValues>();
  const [selectedFile, setSelectedFile] = useState<File | undefined>();

  const handleFileRemoved = () => {
    setSelectedFile(undefined);
    setValue("verificationDocument", undefined);
  };

  const handleFileSelected = (file: File) => {
    setSelectedFile(file);
    setValue("verificationDocument", file);
    logEvent(APP_V2_USER_EVENTS.EMPLOYMENT_VERIFICATION_DOCUMENT_SELECTED, {
      workplaceId,
      shiftId,
      source,
    });
  };

  const handleSubmitReview = () => {
    onAction({
      type: "NEXT_STEP",
      data: { verificationDocument: selectedFile },
    });
  };

  const handleSubmitWithoutVerifying = () => {
    setValue("verificationDocument", undefined);
    onAction({
      type: "NEXT_STEP",
      data: { verificationDocument: undefined },
    });
  };

  return (
    <>
      <DialogContent>
        <Stack spacing={6}>
          <Stack alignItems="center">
            <Illustration type="document-upload" />
          </Stack>

          <Stack spacing={2}>
            <Text semibold variant="h4" textAlign="left">
              Are you an employee here?
            </Text>
            <Text textAlign="left">
              Upload proof of employment to submit your review as a <b>verified employee</b>.
            </Text>
          </Stack>

          <Card
            sx={{
              backgroundColor: (theme) => theme.palette.background.default,
              border: (theme) => `1px solid ${theme.palette.border?.primary}`,
            }}
          >
            <CardContent>
              <Stack spacing={4}>
                <Text semibold variant="body2">
                  Accepted documents
                </Text>
                {ACCEPTED_DOCUMENTS.map((document) => (
                  <Stack key={document.label} direction="row" spacing={3} alignItems="center">
                    <Box
                      sx={{
                        backgroundColor: (theme) => theme.palette.background.tertiary,
                        borderRadius: "50%",
                        padding: 2,
                      }}
                    >
                      <CbhIcon type={document.iconType} size="small" />
                    </Box>

                    <Text variant="body2">{document.label}</Text>
                  </Stack>
                ))}
              </Stack>
            </CardContent>
          </Card>

          <SingleBoxFileUploader
            selectedFile={selectedFile}
            onFileSelected={handleFileSelected}
            onFileRemoved={handleFileRemoved}
          />

          <Text variant="caption" color={(theme) => theme.palette.text.tertiary}>
            Documents are used only for verification and will not be shared publicly or with the
            facility.
          </Text>
        </Stack>
      </DialogContent>

      <DialogFooter orientation="vertical">
        <Stack spacing={3} sx={{ width: "100%" }}>
          <LoadingButton
            fullWidth
            size="large"
            variant="contained"
            isLoading={isSubmitting}
            disabled={!selectedFile}
            onClick={handleSubmitReview}
          >
            Submit review
          </LoadingButton>
          {!isSubmitting && (
            <TextButton sx={{ alignSelf: "center" }} onClick={handleSubmitWithoutVerifying}>
              Submit without verifying
            </TextButton>
          )}
        </Stack>
      </DialogFooter>
    </>
  );
}
